/*
PURPOSE: Defines the application's frontend routes using react-router-dom.
USE CASES:
- Mapping URL paths to specific React components (pages).
- Defining lazy-loaded components for code splitting.
- Handling authentication checks and redirects for protected routes.
- Implementing client-side redirects for short links or legacy paths.
DOMAIN BOUNDARIES:
- Responsible for client-side routing structure and component mapping.
- Interacts with authentication state (via loaders) to control access.
- Should not contain complex business logic directly, but loads components that do.
- Does not handle server-side routing or API definitions.
ASSUMPTIONS:
- Assumes react-router-dom v6+ is used.
- Assumes authentication state can be checked asynchronously (e.g., via Amplify Auth).
- Assumes components for each route exist or are correctly lazy-loaded.
*/
import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, redirect } from 'react-router-dom';
import { getCurrentUser, AuthUser } from '@aws-amplify/auth';
import { getCachedUserProfile, setLoginRedirectUrl } from '../utils/authUtils';
import { User } from 'shared/types/platformTypes';
import App from '../App';
import PageLoader from '../components/PageLoader/PageLoader';

// Landing & Key Public Routes (eagerly loaded)
import Home from '../pages/public-landing/Home/Home';
import PublicCoral from '../pages/public-landing/PublicCoral/PublicCoral';
import PublicArtist from '../pages/public-landing/PublicArtist/PublicArtist';
import ForArtists from '../pages/public-landing/ForArtists/ForArtists';
import Manifesto from '../pages/public-supplementary/Manifesto/Manifesto';
import Welcome from '../pages/platform/Welcome/Welcome';
import RootErrorElement from './RootErrorElement';
import Payment from '../pages/public-landing/Payment/Payment';
import Thankyou from '../pages/public-landing/PublicArtist/Thankyou';
import SignOut from '../pages/platform/SignOut/SignOut';
import VerifyMagicLink from '../pages/platform/VerifyMagicLink/VerifyMagicLink';
import Login from '../pages/platform/Login/Login';
import RequestLogin from '../components/RequestLogin/RequestLogin';
import ArtistPayoutCollection from '../pages/public-landing/PublicArtist/ArtistPayoutCollection';

// Email-related pages (public, no auth required)
const Unsubscribe = lazy(() => import(/* webpackChunkName: "email" */ '../pages/platform/Notifications/Unsubscribe'));
const ArtistOutreachUnsubscribe = lazy(() => import(/* webpackChunkName: "email" */ '../pages/public-landing/ArtistOutreachUnsubscribe/ArtistOutreachUnsubscribe'));
const NewsletterUnsubscribe = lazy(() => import(/* webpackChunkName: "email" */ '../pages/public-landing/NewsletterUnsubscribe/NewsletterUnsubscribe'));

// Static Public Pages (chunk: "public-static")
const StaticPages = {
  About: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-supplementary/About/About')),
  Contact: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-supplementary/Contact/Contact')),
  Privacy: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-policies/Privacy/Privacy')),
  Terms: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-policies/Terms/Terms')),
  ArtistTerms: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-policies/Terms/ArtistTerms')),
  CommonTerms: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-policies/CommonTerms/CommonTerms')),
  TicketsHostTerms: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-policies/tickets/HostTerms')),
  TicketsBuyerTerms: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-policies/tickets/BuyerTerms')),
  TicketsRefundPolicy: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-policies/tickets/RefundPolicy')),
  LegalIndex: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-policies/LegalIndex/LegalIndex')),
  Principles: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-supplementary/Principles/Principles')),
  Trust: lazy(() => import(/* webpackChunkName: "public-static" */ '../pages/public-supplementary/Trust/Trust'))
};

// Protected User Pages (chunk: "user")
const UserPages = {
  Dashboard: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/Dashboard/Dashboard')),
  OurImpact: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/OurImpact/OurImpact')),
  Settings: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/Settings/Settings')),
  Notifications: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/Notifications/Notifications')),
  ActivityDetail: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/ActivityDetail/ActivityDetailPage')),
  Coral: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/Coral/Coral')),
  CrossUserCoralView: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/CrossUserCoral/CrossUserCoralView')),
  CreateCoralFunnel: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/CreateCoralFunnel/CreateCoralFunnel')),
  EditCoral: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/Coral/EditCoral')),
  UserIdentityVerification: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/ArtistSetup/UserIdentityVerificationForm')),
  ArtistDetailedVerification: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/ArtistSetup/ArtistDetailedVerification')),
  ArtistConnections: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/ArtistSetup/ArtistConnections')),
  ProfileSetup: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/ProfileSetup/ProfileSetup')),
  ArtistPage: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/Artist/ArtistPage')),
  UserProfilePage: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/UserProfile/UserProfilePage')),
  ListeningRoom: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/ListeningRoom/ListeningRoomPage')),
  CommunityPage: lazy(() => import(/* webpackChunkName: "user" */ '../pages/platform/Community/CommunityPage')),
};

// Admin Pages (chunk: "admin")
const AdminPages = {
  Landing: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/Admin/AdminLandingPage')),
  MissingArtists: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/Admin/Artist/MissingArtists/MissingArtists')),
  IdentityVerification: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/Admin/Artist/Verification/IdentifyVerification')),
  ArtistApplication: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/Admin/Artist/Verification/ArtistApplicationAdmin')),
  ArtistPayments: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/Admin/Artist/ArtistPayments/ArtistPayments')),
  ArtistMetadata: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/Admin/Artist/ArtistMetadata/ArtistMetadata')),
  ArtistOutreach: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/Admin/Artist/ArtistOutreach/ArtistOutreach')),
  MailingLists: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/MailingLists/MailingLists')),
  RefundQueue: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/RefundQueue/RefundQueue')),
  Accounting: lazy(() => import(/* webpackChunkName: "admin" */ '../pages/platform-admin/Admin/Accounting/Accounting'))
};


/**
 * Loader data returned to routes
 */
interface LoaderData {
  currentUser: AuthUser | null;
  userProfile: User | null;
}

/**
 * Base loader configuration options
 */
interface LoaderOptions {
  requireAuth?: boolean;           // Requires user to be authenticated
  requireContributor?: boolean;    // Requires contributor access level
  redirectIfAuth?: string;         // Redirect authenticated users to this path
  allowPublicView?: boolean;       // Allow public view even if authenticated
}

/**
 * Base loader function that consolidates auth, redirect, and access control logic
 * All route loaders should use this to ensure consistent behavior
 */
function createBaseLoader(options: LoaderOptions = {}): ({ request }: { request: Request }) => Promise<LoaderData | Response> {
  return async ({ request }: { request: Request }): Promise<LoaderData | Response> => {
    try {
      const url = new URL(request.url);
      const currentUser = await getCurrentUser().catch(() => null);

      // Public view - no auth required, no redirects
      if (options.allowPublicView) {
        const userProfile = currentUser ? await getCachedUserProfile(currentUser.userId) : null;
        return { currentUser, userProfile };
      }

      // Redirect authenticated users (e.g., homepage → dashboard)
      if (currentUser && options.redirectIfAuth) {
        return redirect(options.redirectIfAuth);
      }

      // Public route - no auth required
      if (!options.requireAuth && !options.requireContributor) {
        const userProfile = currentUser ? await getCachedUserProfile(currentUser.userId) : null;
        return { currentUser, userProfile };
      }

      // Auth required routes
      if (!currentUser) {
        // Store the intended destination for post-login redirect
        const intendedPath = url.pathname + url.search;
        setLoginRedirectUrl(intendedPath);
        return redirect('/login');
      }

      const userProfile = await getCachedUserProfile(currentUser.userId);

      // Check profile setup completion
      if (url.pathname !== '/setup' && !userProfile?.settings?.profileSetupComplete) {
        return redirect('/setup');
      }

      // Check contributor access if required
      if (options.requireContributor) {
        const userAccessLevel = userProfile.accessLevel || 'member';

        if (userAccessLevel !== 'contributor') {
          // Store intended destination for after upgrade
          const intendedPath = url.pathname + url.search;
          sessionStorage.setItem('intendedDestination', intendedPath);
          return redirect('/dashboard?access=denied');
        }
      }

      return { currentUser, userProfile };
    } catch (error) {
      console.error('Loader error:', error);
      return redirect('/login');
    }
  };
}

// Specialized loaders using the base loader
const homeLoader = createBaseLoader({ redirectIfAuth: '/dashboard' });
const loginLoader = createBaseLoader({ redirectIfAuth: '/dashboard' }); // Login page redirects authenticated users
const authLoader = createBaseLoader({ requireAuth: true });
const publicLoader = createBaseLoader({ allowPublicView: true });
const contributorLoader = createBaseLoader({ requireAuth: true, requireContributor: true });

async function profileRedirectLoader() {
  try {
    const currentUser = await getCurrentUser();
    if (!currentUser) {
      setLoginRedirectUrl('/profile');
      return redirect('/login');
    }
    return redirect(`/u/${currentUser.userId}`);
  } catch (error) {
    return redirect('/login');
  }
}


export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      errorElement: <RootErrorElement />,
      children: [
      // Homepage - redirects logged-in users to dashboard
      {
        index: true,
        element: <Home />,
        loader: homeLoader,
      },
      // Landing page - always shows public homepage (no redirect)
      {
        path: '/landing',
        element: <Home />,
        loader: publicLoader,
      },
      {
        path: '/c/:id',
        element: <PublicCoral />,
        loader: publicLoader,
      },
      {
        path: '/a/:id',
        element: <PublicArtist />,
        loader: publicLoader,
      },
      {
        path: '/welcome',
        element: <Welcome />,
        loader: publicLoader,
      },
      {
        path: '/about',
        element: <Suspense fallback={<PageLoader />}><StaticPages.About /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/for-artists',
        element: <ForArtists />,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/privacy',
        element: <Suspense fallback={<PageLoader />}><StaticPages.Privacy /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/terms',
        element: <Suspense fallback={<PageLoader />}><StaticPages.Terms /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/contact-us',
        element: <Suspense fallback={<PageLoader />}><StaticPages.Contact /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },

      // Additional public routes
      {
        path: '/payment/:id',
        element: <Payment />,
        loader: publicLoader,
      },
      {
        path: '/thankyou',
        element: <Thankyou />,
        loader: publicLoader,
      },
      {
        path: '/sign-out',
        element: <SignOut />,
        loader: publicLoader,
      },
      {
        path: '/auth/verify',
        element: <VerifyMagicLink />,
        loader: publicLoader,
      },
      {
        path: '/login',
        element: <Login />,
        loader: loginLoader,
      },
      {
        path: '/requestlogin',
        element: <RequestLogin />,
        loader: publicLoader,
      },
      {
        path: '/a/:artistUrl/collect',
        element: <ArtistPayoutCollection />,
        loader: publicLoader,
      },
      {
        path: '/notifications/unsubscribe',
        element: <Suspense fallback={<PageLoader />}><Unsubscribe /></Suspense>,
        loader: publicLoader,
      },
      {
        path: '/artist/outreach/unsubscribe',
        element: <Suspense fallback={<PageLoader />}><ArtistOutreachUnsubscribe /></Suspense>,
        loader: publicLoader,
      },
      {
        path: '/newsletter/unsubscribe',
        element: <Suspense fallback={<PageLoader />}><NewsletterUnsubscribe /></Suspense>,
        loader: publicLoader,
      },

      // Additional static pages
      {
        path: '/artist-terms',
        element: <Suspense fallback={<PageLoader />}><StaticPages.ArtistTerms /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/legal',
        element: <Suspense fallback={<PageLoader />}><StaticPages.LegalIndex /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/legal/common-terms',
        element: <Suspense fallback={<PageLoader />}><StaticPages.CommonTerms /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/tickets/host-terms',
        element: <Suspense fallback={<PageLoader />}><StaticPages.TicketsHostTerms /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/tickets/terms',
        element: <Suspense fallback={<PageLoader />}><StaticPages.TicketsBuyerTerms /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/tickets/refund-policy',
        element: <Suspense fallback={<PageLoader />}><StaticPages.TicketsRefundPolicy /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/principles',
        element: <Suspense fallback={<PageLoader />}><StaticPages.Principles /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/trust',
        element: <Suspense fallback={<PageLoader />}><StaticPages.Trust /></Suspense>,
        // Prerendered marketing page: no loader so content renders on first paint.
      },
      {
        path: '/manifesto',
        element: <Manifesto />,
        // Prerendered marketing page: no loader so content renders on first paint.
      },

      // Account routes
      {
        path: '/dashboard',
        element: <Suspense fallback={<PageLoader />}><UserPages.Dashboard /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/dancefloor',
        loader: () => redirect('/dashboard?tab=dancefloor'),
      },
      {
        path: '/settings',
        element: <Suspense fallback={<PageLoader />}><UserPages.Settings /></Suspense>,
        loader: authLoader,
      },
      {
        // Delivered email carries this path; it resolves to the tab that owns those toggles.
        path: '/settings/preferences',
        loader: () => redirect('/settings?tab=notifications'),
      },
      {
        path: '/notifications',
        element: <Suspense fallback={<PageLoader />}><UserPages.Notifications /></Suspense>,
        loader: contributorLoader,
      },
      {
        path: '/activity/:contentId',
        element: <Suspense fallback={<PageLoader />}><UserPages.ActivityDetail /></Suspense>,
        loader: contributorLoader,
      },
      {
        path: '/setup',
        element: <Suspense fallback={<PageLoader />}><UserPages.ProfileSetup /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/coral',
        element: <Suspense fallback={<PageLoader />}><UserPages.Coral /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/impact',
        element: <Suspense fallback={<PageLoader />}><UserPages.OurImpact /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/listening-room',
        element: <Suspense fallback={<PageLoader />}><UserPages.ListeningRoom /></Suspense>,
        loader: contributorLoader,
      },
      {
        path: '/listening-room/pick/:contentId',
        element: <Suspense fallback={<PageLoader />}><UserPages.ListeningRoom /></Suspense>,
        loader: contributorLoader,
      },
      {
        path: '/coral/:id',
        element: <Suspense fallback={<PageLoader />}><UserPages.Coral /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/u/:userId/coral/:coralId',
        element: <Suspense fallback={<PageLoader />}><UserPages.CrossUserCoralView /></Suspense>,
        loader: contributorLoader,
      },
      {
        path: '/u/:userId',
        element: <Suspense fallback={<PageLoader />}><UserPages.UserProfilePage /></Suspense>,
        loader: contributorLoader,
      },

      // Additional protected routes
      {
        path: '/verify-identity',
        element: <Suspense fallback={<PageLoader />}><UserPages.UserIdentityVerification /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/artist-applications',
        element: <Suspense fallback={<PageLoader />}><UserPages.ArtistDetailedVerification /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/artist-connections',
        element: <Suspense fallback={<PageLoader />}><UserPages.ArtistConnections /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/create-coral',
        element: <Suspense fallback={<PageLoader />}><UserPages.CreateCoralFunnel /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/coral/:id/edit',
        element: <Suspense fallback={<PageLoader />}><UserPages.EditCoral /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/artist/:artistId',
        element: <Suspense fallback={<PageLoader />}><UserPages.ArtistPage /></Suspense>,
        loader: contributorLoader,
      },
      {
        path: '/community/:communityId',
        element: <Suspense fallback={<PageLoader />}><UserPages.CommunityPage /></Suspense>,
        loader: contributorLoader,
      },
      {
        path: '/profile',
        loader: profileRedirectLoader,
      },
      {
        path: '/profile/:userId',
        loader: ({ params }) => redirect(`/u/${params.userId}`),
      },

      // Admin routes
      {
        path: '/admin/home',
        element: <Suspense fallback={<PageLoader />}><AdminPages.Landing /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/artist/payments',
        element: <Suspense fallback={<PageLoader />}><AdminPages.ArtistPayments /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/accounting',
        element: <Suspense fallback={<PageLoader />}><AdminPages.Accounting /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/artist/outreach',
        element: <Suspense fallback={<PageLoader />}><AdminPages.ArtistOutreach /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/artist/missing',
        element: <Suspense fallback={<PageLoader />}><AdminPages.MissingArtists /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/artist/identity-verification',
        element: <Suspense fallback={<PageLoader />}><AdminPages.IdentityVerification /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/artist/applications',
        element: <Suspense fallback={<PageLoader />}><AdminPages.ArtistApplication /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/artist-metadata',
        element: <Suspense fallback={<PageLoader />}><AdminPages.ArtistMetadata /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/mailing-lists',
        element: <Suspense fallback={<PageLoader />}><AdminPages.MailingLists /></Suspense>,
        loader: authLoader,
      },
      {
        path: '/admin/refunds',
        element: <Suspense fallback={<PageLoader />}><AdminPages.RefundQueue /></Suspense>,
        loader: authLoader,
      },

      // --- Redirects ---
      {
        path: '/homebrewed',
        loader: () => redirect('https://open.spotify.com/episode/4N4p32j4MsVrS3Zih7diwQ?si=af8e68d0c0544987', 301), // 301 for permanent redirect
      },
    ],
  },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_startTransition: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true
    }
  }
);